// The Roll Positions "skill" seam.
//
// ⚠ WHAT THIS SEAM MEANS NOW (CR-20260910-001 milestone 1, contract A1 v0.2 §8). It used to hold a
// SCORING BRAIN that ran in the browser: the orchestrator handed it the loaded Price Box cells and
// it filtered, ranked and explained them. That brain — about 1,150 lines across the skill, the
// ranking, the window and the exclusion resolvers — now lives in the backend `ApgOrion` module, so
// that the APG ORION keeps its cards while running as a CLIENT, and so that Maven can order the
// same object in process.
//
// The seam survives the migration deliberately. Keeping it is what made this a client swap rather
// than an orchestrator rewrite: `ApgCopilotComponent` still injects one token, calls one method and
// renders what comes back. What changed is behind it — `BackendApgOrionSkill` sends
// `RunApgOrionSkill` and awaits `ApgOrionCardsReadyDto` over the event stream.
//
// ⚠ WHAT LEFT THIS FILE, AND WHY IT IS NOT COMING BACK:
//   - `RollCandidate` — it existed to hand BROWSER-READ Price Box cells to a browser skill. The
//     order form carries settings, dates and exclusions; it never carries cells. The Price Box is
//     now populated FROM the delivery, not read INTO the run.
//   - `RollRunReport` + the coverage note — removed by ruling R-C. See `apg-copilot-domain.ts`.
//   - the landmine map, the resolved window, the user-excluded list, the filtered-out list and the
//     top-N — all four are the backend's job now, or (top-N) a display limit applied at render.

import { InjectionToken } from '@angular/core';
import { AdjustmentPricingSettingsDto } from '../../shell-communication/shell-operations-protocol';
import { CashFlowAdjustment } from '../model/CashFlowAdjustment';
import { RollSuggestionContent } from './apg-copilot-domain';

/**
 * The order form, in the panel's own words.
 *
 * ⚠ `adjustmentPricingSettings` travels with its `customDates` LEFT EMPTY, and that is deliberate.
 * The backend resolves the window itself (contract A1 §4.1) — it must, because Maven builds the
 * same settings array on the backend from a template id and has no browser to ask (contract M1 §3,
 * A1 §6). Resolving the window here as well would put one rule in two languages, which is exactly
 * what this migration removes. The dates and the three exclusion lists below are everything the
 * backend needs to derive it.
 */
export interface ApgOrionRunRequest {
    /** Scopes the backend run registry to (terminalId, panelInstanceId), which is how supersede is scoped. */
    panelInstanceId: string;
    portfolioId: string;
    /** The expanded template settings, one per strategy, each with its flat before-state. */
    adjustmentPricingSettings: AdjustmentPricingSettingsDto[];
    dateKind: string;                      // "single" | "range"
    date: string | null;                   // single: 'yyyy-MM-dd'
    rangeStart: string | null;             // range: 'yyyy-MM-dd'
    rangeEnd: string | null;               // range: 'yyyy-MM-dd'
    // Sent as the trader set them, NOT pre-emptied for a single date. BR-W2 ("a single date takes
    // no exclusions") is a RULE, and the rule lives on the backend now with the rest of them.
    weekdayExclusions: string[];
    specificDateExclusions: string[];
    // Days brought back from the weekday rule - any excluded weekday since CR-20260915-002.
    weekdayOverrides: string[];
}

/**
 * What a run delivers to the orchestrator.
 *
 * `contents` is EVERY calculated date, ranked — there is no backend cap (ruling R-B, guarantee
 * A-G3). The "show top N" setting is applied by the results list when it renders, and the Price Box
 * feeds its columns from the same delivery, which is why a backend cut would blank them.
 */
export interface ApgOrionRunResult {
    contents: RollSuggestionContent[];

    /**
     * THE PRICE BOX'S FEED: every adjustment the run calculated for the window, flattened — the
     * cards' own adjustments PLUS the delivery's `windowAdjustments` (leader ruling L37). The grid
     * renders its columns from this and prices them live from its own strategy-code subscriptions.
     *
     * ⚠ IT IS A UNION, AND IT IS NOT DE-DUPLICATED (leader ruling L50). A card is one CELL, not one
     * date, and a no-zone portfolio prices two cells on the same date — so a date can legitimately
     * appear more than once here. Removing a "duplicate" date would delete a real column.
     *
     * ⚠ IT IS NOT DERIVABLE FROM `contents`. A Monday or a report day never becomes a card and is
     * only ever in this array; that is the entire reason it exists. Never rebuild it by flattening
     * the cards.
     */
    priceBoxAdjustments: CashFlowAdjustment[];
    /** The backend's non-fatal notes (`ZoneUnavailable`, …). NOT a dropped-day report — R-C. */
    warnings: string[];
    /** True for the backend's `empty` outcome: the run finished and produced nothing at all. */
    empty: boolean;
}

export interface IRollPositionsSkill {
    /**
     * Send the questionnaire, resolve with the cards. An empty card list is a VALID outcome, not an
     * error. Rejects on `superseded`, `terminalFailed` and `preSendBlocked`, with the outcome
     * readable off the error (see `apgOrionRejectionOutcomeOf`).
     */
    run(request: ApgOrionRunRequest): Promise<ApgOrionRunResult>;
}

export const ROLL_POSITIONS_SKILL = new InjectionToken<IRollPositionsSkill>('ROLL_POSITIONS_SKILL');
